import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { track, amountBand } from '../../../telemetry/journey.js';
import { ActionButtonWrapper, BackButton, EvHeader, Notice } from '../../../components';
import { MdClose } from "react-icons/md";
import { FaDollarSign } from "react-icons/fa6";
import { useSession } from '../../../context/SessionContext.jsx';
import { useToast } from '../../../context/ToastContext.jsx';
import { useAuth } from '../../../context/AuthContext.jsx';
import useElementHeight from '../../../hooks/useElementHeight.js';
import { validateRedeemCode } from '../../../api/redeemCodeApi.js';
import { listMyRedeemCodes } from '../../../api/redeemCodesApi.js';
import { computeCreditBreakdown } from './computeCreditBreakdown.js';
import { isGuestChoice } from '../../../constants/accountChoice.js';
import { useCancelAttempt } from '../../../hooks/useCancelAttempt.jsx';
import './AmountPicker.less';

const amounts = [20, 50, 100, 200]; // shown as BBD dollars

const toCents = (v) => {
    // tolerate "50", "50.00"
    const n = Number(String(v).replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
    return Math.round(n * 100);
};
const formatCurrency = (cents, currency = 'BBD') => {
    try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format((cents || 0) / 100);
    } catch {
        return `${((cents || 0) / 100).toFixed(2)} ${currency}`;
    }
};

export default function AmountPicker({ handleIncreaseStep, minCents = 1000, maxCents = 500000 }) {
    const { state, setAmount, update, ensureCustomerSession } = useSession();
    const { amountCents, currency, stationMeta, appliedCredit } = state;
    const { showToast } = useToast();
    const { user } = useAuth();
    const isAuthenticated = !!user?.id;

    // Leaving the Pre-Authorization screen -- header Back AND Cancel -- goes
    // through one path. The attempt already exists by this screen (created by
    // the plate screen's Continue, or here for a guest), so leaving must cancel
    // it on the server. Both used to leave it pending: Back only stepped back,
    // and Cancel was intercepted by ActionButtonWrapper's built-in 'cancel',
    // which confirms and then resets LOCAL state only.
    const { confirmAndCancel } = useCancelAttempt();

    // ─────────────────────────────────────────────────────────────
    // Redeem-code UI state. Applied credit lives in SessionContext
    // (`state.appliedCredit`) so it survives navigation between the
    // Pre-Auth Hold, Confirm & Pay, and payment steps. Validation is
    // a pre-flight check only — the server does not consume the code
    // until payment bootstrap succeeds.
    // ─────────────────────────────────────────────────────────────
    const [redeemPanelOpen, setRedeemPanelOpen] = useState(false);
    const [redeemInput, setRedeemInput] = useState('');
    const [redeemStatus, setRedeemStatus] = useState('idle'); // 'idle' | 'validating' | 'error'
    const [redeemError, setRedeemError] = useState('');

    // ─────────────────────────────────────────────────────────────
    // Account-code picker (authenticated users only).
    //
    // Fetches the signed-in user's own redeem codes via the JWT-
    // scoped `/api/redeem-codes/mine` endpoint (same helper the
    // Redeem Codes page uses — no duplicated fetch logic). Only
    // renders when `isAuthenticated`. Guests never trigger this
    // fetch and see the manual-entry UI they always saw.
    //
    // Selecting an account code updates `appliedCredit` LOCALLY only
    // — we already own the code's amount/currency from the list
    // response, so there's no need to call the anonymous
    // `/validate` endpoint again. Actual redemption still happens
    // atomically on the backend at session-start; if the backend
    // rejects the code (state changed since we listed it),
    // ChargeSetup already clears `appliedCredit` and sends the user
    // back here to reselect.
    // ─────────────────────────────────────────────────────────────
    const [myCodes, setMyCodes] = useState({
        loading: false,
        loaded: false,
        codes: [],
        error: null,
    });

    const loadMyCodes = useCallback(async () => {
        setMyCodes((s) => ({ ...s, loading: true, error: null }));
        const result = await listMyRedeemCodes();
        if (result.ok) {
            setMyCodes({
                loading: false,
                loaded: true,
                codes: Array.isArray(result.codes) ? result.codes : [],
                error: null,
            });
        } else {
            setMyCodes({
                loading: false,
                loaded: true,
                codes: [],
                error: result.error || 'LIST_FAILED',
            });
        }
    }, []);

    useEffect(() => {
        // Fire once for signed-in users. Guests never call the endpoint
        // (it would 401 anyway; skipping avoids the noise).
        if (!isAuthenticated) return;
        loadMyCodes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    // ── Session boundary for the guest path ──────────────────────────
    //
    // Session creation used to live on CHARGE_SETUP's Continue. Guests skip
    // that screen, so without this they would reach the card with no session
    // to pay for. `ensureCustomerSession` is the existing idempotent boundary
    // (exactly one POST /api/ev/sessions per ensure cycle), already used by
    // VehicleVerification, so the two paths converge on one implementation
    // rather than a second copy of the create logic.
    //
    // Deliberately here rather than on the choice screen: creating a session
    // claims the connector, and AccountChoice runs BEFORE connector selection
    // — there is no connectorId to create against yet. Claiming late also
    // holds the connector for less time while a driver picks an amount.
    useEffect(() => {
        if (!isGuestChoice(state.accountChoice)) return;
        if (state.sessionUid) return;
        if (!state.stationId || state.connectorId == null) return;

        let cancelled = false;
        (async () => {
            const result = await ensureCustomerSession();
            if (cancelled || result?.ok) return;
            showToast({
                message: "We couldn't start your session. Please try again.",
                type: 'error',
            });
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.accountChoice, state.sessionUid, state.stationId, state.connectorId]);

    // Only `active` codes are eligible for selection. `effectiveStatus`
    // on the backend already downgrades an active-but-expired row to
    // 'expired' before we see it, so a simple equality check is
    // enough — no client-side expiry math.
    const availableCodes = useMemo(
        () => myCodes.codes.filter((c) => c && c.status === 'active'),
        [myCodes.codes],
    );

    // Which radio is currently checked. `appliedCredit` in
    // SessionContext is the source of truth for the applied code;
    // the picker mirrors it. If a manually-entered code is applied
    // that isn't in the account list, we treat that as "manual".
    const selectedAccountCode = availableCodes.find(
        (c) => appliedCredit && c.code === appliedCredit.code,
    );
    const pickerSelection = !appliedCredit
        ? 'none'
        : selectedAccountCode
        ? `account:${selectedAccountCode.code}`
        : 'manual';

    const showAccountPicker =
        isAuthenticated && (myCodes.loading || availableCodes.length > 0 || myCodes.error);

    const handleApplyRedeemCode = async () => {
        const codeTrim = String(redeemInput || '').trim().toUpperCase();
        setRedeemError('');
        if (!codeTrim) {
            setRedeemError('Please enter a redeem code.');
            return;
        }
        setRedeemStatus('validating');
        try {
            const result = await validateRedeemCode({ code: codeTrim });
            if (result.valid) {
                update({
                    appliedCredit: {
                        code:     result.code,
                        amount:   Number(result.amount),
                        currency: result.currency,
                    },
                });
                setRedeemInput('');
                setRedeemPanelOpen(false);
                setRedeemStatus('idle');
                setRedeemError('');
                showToast({
                    message: 'Credit code applied.',
                    type: 'success',
                    duration: 2500,
                });
            } else {
                setRedeemStatus('error');
                setRedeemError(result.message || 'Invalid code.');
            }
        } catch {
            setRedeemStatus('error');
            setRedeemError('Could not validate code. Please try again.');
        }
    };

    const handleRemoveRedeemCode = () => {
        update({ appliedCredit: null });
        setRedeemInput('');
        setRedeemStatus('idle');
        setRedeemError('');
    };

    // Radio: "No redeem code" — clear any applied code + close the
    // manual entry panel.
    const handleSelectNoCode = () => {
        update({ appliedCredit: null });
        setRedeemPanelOpen(false);
        setRedeemInput('');
        setRedeemStatus('idle');
        setRedeemError('');
    };

    // Radio: account code — stash the amount/currency we already
    // hold from the list. NO extra `/validate` roundtrip: this is
    // strictly local UI state per the product spec ("Selection is
    // only local UI state"). The backend authoritatively validates
    // + redeems at session-start.
    const handleSelectAccountCode = (code) => {
        setRedeemPanelOpen(false);
        setRedeemInput('');
        setRedeemStatus('idle');
        setRedeemError('');
        update({
            appliedCredit: {
                code: code.code,
                amount: Number(code.amount),
                currency: code.currency || 'BBD',
            },
        });
    };

    // Radio: "Enter another code" — reveal the existing manual
    // input. Doesn't apply anything until the user types + hits
    // Apply, which goes through the pre-existing anonymous
    // /validate flow.
    const handleSelectManualEntry = () => {
        // Only clear a previously-applied code if it was an account
        // one; a code already sitting in the manual input flow
        // stays applied so the user can Remove it deliberately.
        if (selectedAccountCode) {
            update({ appliedCredit: null });
        }
        setRedeemPanelOpen(true);
    };
    
    // Get minimum charge from stationMeta (in dollars), convert to cents
    // Default to $10.00 if not specified
    const minChargeFromStation = stationMeta?.minCharge 
        ? Math.round(stationMeta.minCharge * 100) 
        : minCents;
    const effectiveMinCents = Math.max(minChargeFromStation, minCents);

    const [isCustomAmountEnabled, setIsCustomAmountEnabled] = useState(false);
    const [customAmount, setCustomAmount] = useState('');     // dollars as text
    const [selectedAmount, setSelectedAmount] = useState(null); // dollars as number
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { ref } = useElementHeight();

    // Restore UI from session on mount
    useEffect(() => {
        if (amountCents > 0) {
        const dollars = (amountCents / 100);
        // If it matches one of the preset buttons, show it as selected; else show custom
        if (amounts.includes(dollars)) {
            setSelectedAmount(dollars);
            setIsCustomAmountEnabled(false);
            setCustomAmount('');
        } else {
            setSelectedAmount(null);
            setIsCustomAmountEnabled(true);
            setCustomAmount(dollars.toFixed(2));
        }
        }
    }, [amountCents]);

    const effectiveCents = useMemo(() => {
        if (isCustomAmountEnabled && customAmount !== '') {
        return toCents(customAmount);
        }
        if (selectedAmount != null) {
        return toCents(selectedAmount);
        }
        return 0;
    }, [isCustomAmountEnabled, customAmount, selectedAmount]);

    // Credit reduces the effective card authorization. Server is the
    // final authority — this is a UI preview so the customer can see
    // "Card Authorization Needed: X BBD" before continuing.
    //
    // The pure math lives in computeCreditBreakdown so the rules are
    // unit-testable without booting React. codeCents is the raw code
    // value; creditAppliedCents is what actually reduces the card auth.
    const codeCents = appliedCredit
        ? Math.round(Number(appliedCredit.amount || 0) * 100)
        : 0;
    const {
        creditAppliedCents,
        cardAuthCents,
        fullyCovered: isFullyCovered,
        hasCarryForward,
        creditWithoutBudget,
    } = computeCreditBreakdown({
        selectedBudgetCents: effectiveCents,
        codeAmountCents:     codeCents,
    });

    // Continue button label reflects final behaviour:
    //   effective > 0 → card authorization needed → "Continue to Secure Payment"
    //   effective = 0 → no Fiserv → "Start Charging"
    // Falls back to a plain "Continue" when no amount is chosen yet.
    const continueLabel = (() => {
        if (!effectiveCents) return 'Continue';
        if (isFullyCovered)  return 'Start Charging';
        return `Continue to Secure Payment • ${formatCurrency(cardAuthCents, currency)}`;
    })();

    const actions = [
        {
            id: 'continue',
            label: continueLabel,
            type: 'btn-dark',
            disabled: false  // Always enabled!
        },
        // onClick takes precedence over ActionButtonWrapper's built-in
        // local-only 'cancel' handling.
        { id: 'cancel', label: 'Cancel', type: 'btn-outline-dark', onClick: confirmAndCancel }
    ];

    const onContinue = async (actionId) => {
        if (actionId === 'cancel') {
            return confirmAndCancel();
        }
        
        // Prevent double-clicks while submitting
        if (isSubmitting) return;
        
        // Validation with snackbar feedback. A pre-auth budget is
        // ALWAYS required — the credit reduces it but does not
        // replace it. If a code is applied but no budget was chosen
        // yet, surface a credit-specific message so the customer
        // understands the credit alone isn't enough.
        if (!effectiveCents) {
            showToast({
                message: appliedCredit
                    ? 'Select a pre-authorization amount to apply this credit.'
                    : 'Please select an amount or enter a custom amount.',
                type: 'error'
            });
            return;
        }
        
        if (effectiveCents < effectiveMinCents) {
            const minDollars = (effectiveMinCents / 100).toFixed(2);
            showToast({
                message: `Minimum charge amount is $${minDollars} ${currency}.`,
                type: 'error'
            });
            return;
        }
        
        if (effectiveCents > maxCents) {
            const maxDollars = (maxCents / 100).toFixed(2);
            showToast({
                message: `Maximum top-up is $${maxDollars} ${currency}.`,
                type: 'error'
            });
            return;
        }
        
        // Validation passed - proceed
        try {
            setIsSubmitting(true);
            // clamp for UX; server still validates actual payable
            const clamped = Math.max(minCents, Math.min(effectiveCents, maxCents));
            setAmount(clamped, currency);               // ⟵ persist in SessionContext
            track('charging.amount.selected', { metadata: { amount_band: amountBand(clamped / 100) } });
            await handleIncreaseStep();                 // advance flow
        } catch {
            showToast({
                message: 'Failed to continue. Please try again.',
                type: 'error'
            });
            setIsSubmitting(false);
        }
    };

    return (
        <div className='ev-content-wrapper'>
            <EvHeader
                headerAction={{
                label: <><a><BackButton /></a></>,
                action: confirmAndCancel
                }}
            />

            <div className="ev-inner-profile-wrapper">
                <div className="amount-picker-header">
                    <h2 className='heading'>Pre-Authorization Hold</h2>
                    <p className="amount-picker-subtitle">
                        Select a pre-authorization hold amount. You will only be charged for the energy used — any unused balance will be released back to your card.
                    </p>
                    {effectiveMinCents > 0 && (
                        <Notice variant="light" icon={false} className="amount-picker-min-notice">
                            Minimum charge amount is {formatCurrency(effectiveMinCents, currency)}
                        </Notice>
                    )}
                </div>

                <div className="amount-picker-card-container">
                    {!isCustomAmountEnabled && (
                        <div className="amount-blocks-wrapper">
                            {amounts.map((amount) => {
                                const active = selectedAmount === amount;
                                return (
                                    <button
                                        key={amount}
                                        className={`amount-btn ${active ? 'active' : ''}`}
                                        onClick={() => {
                                            setSelectedAmount(amount);
                                            setCustomAmount('');
                                        }}
                                        aria-pressed={active}
                                    >
                                        <FaDollarSign size={16} />
                                        <span>{amount}</span>
                                    </button>
                                );
                            })}

                            <button
                                className="amount-btn"
                                onClick={() => {
                                    setIsCustomAmountEnabled(true);
                                    setSelectedAmount(null);
                                    setCustomAmount('');
                                }}
                            >
                                Custom
                            </button>
                        </div>
                    )}

                    {isCustomAmountEnabled && (
                        <form
                            className="custom-amount-wrapper"
                            onSubmit={(e) => {
                                e.preventDefault();
                                if (!isSubmitting) onContinue('continue');
                            }}
                        >
                            <div className="custom-amount-input-wrapper">
                                <label htmlFor="custom-amount-input" className="gc-visually-hidden">
                                    Custom charging budget in BBD
                                </label>
                                <FaDollarSign className='input-icon' size={16} aria-hidden="true" />
                                <input
                                    id="custom-amount-input"
                                    type="text"
                                    inputMode="decimal"
                                    className="custom-amount-input"
                                    placeholder="Enter amount (BBD)"
                                    value={customAmount}
                                    onChange={(e) => setCustomAmount(e.target.value)}
                                    onBlur={(e) => {
                                        // normalize on blur
                                        const cents = toCents(e.target.value);
                                        setCustomAmount(cents ? (cents / 100).toFixed(2) : '');
                                    }}
                                    autoFocus
                                    enterKeyHint="go"
                                />
                            </div>

                            <button
                                type="button"
                                className="close-custom-btn"
                                onClick={() => {
                                    setIsCustomAmountEnabled(false);
                                    setCustomAmount('');
                                    setSelectedAmount(null);
                                }}
                                aria-label="Back to preset amounts"
                            >
                                <MdClose size={20} />
                                Back to Presets
                            </button>
                        </form>
                    )}
                </div>

                {/* Redeem / credit code section.
                    ─────────────────────────────────────────────────
                    Authenticated users with account-owned codes see
                    a radio picker of their own codes plus an
                    "Enter another code" option that reveals the
                    pre-existing manual entry flow. Guests (or
                    signed-in users with no active account codes)
                    see the manual-entry-only UI they always saw.
                    ───────────────────────────────────────────────── */}
                <div className="redeem-code-section" data-testid="redeem-code-section">
                    <div className="redeem-code-heading">Apply redeem code</div>

                    {showAccountPicker ? (
                        <fieldset className="redeem-code-picker" data-testid="account-code-picker">
                            <legend className="sr-only">Choose a redeem code</legend>

                            {/* Loading — first fetch is in flight, no
                                codes yet to show. */}
                            {myCodes.loading && !myCodes.loaded && (
                                <p
                                    className="redeem-code-loading"
                                    role="status"
                                    aria-live="polite"
                                >
                                    Loading your redeem codes…
                                </p>
                            )}

                            {/* Once loaded, always show "No redeem code" as
                                the neutral choice. */}
                            {myCodes.loaded && (
                                <>
                                    <label className="redeem-code-option">
                                        <input
                                            type="radio"
                                            name="redeem-code-select"
                                            value="none"
                                            checked={pickerSelection === 'none'}
                                            onChange={handleSelectNoCode}
                                            disabled={isSubmitting}
                                        />
                                        <span className="redeem-code-option-body">
                                            <span className="redeem-code-option-title">
                                                No redeem code
                                            </span>
                                        </span>
                                    </label>

                                    {availableCodes.map((c) => {
                                        const value = `account:${c.code}`;
                                        return (
                                            <label className="redeem-code-option" key={c.id ?? c.code}>
                                                <input
                                                    type="radio"
                                                    name="redeem-code-select"
                                                    value={value}
                                                    checked={pickerSelection === value}
                                                    onChange={() => handleSelectAccountCode(c)}
                                                    disabled={isSubmitting}
                                                />
                                                <span className="redeem-code-option-body">
                                                    <span className="redeem-code-option-title">
                                                        {c.code}
                                                    </span>
                                                    <span className="redeem-code-option-meta">
                                                        {Number(c.amount).toFixed(2)} {c.currency || 'BBD'} available
                                                        {c.expiresAt && (
                                                            <> · expires {new Date(c.expiresAt).toLocaleDateString()}</>
                                                        )}
                                                    </span>
                                                </span>
                                            </label>
                                        );
                                    })}

                                    <label className="redeem-code-option">
                                        <input
                                            type="radio"
                                            name="redeem-code-select"
                                            value="manual"
                                            checked={pickerSelection === 'manual' || redeemPanelOpen}
                                            onChange={handleSelectManualEntry}
                                            disabled={isSubmitting}
                                        />
                                        <span className="redeem-code-option-body">
                                            <span className="redeem-code-option-title">
                                                Enter another code
                                            </span>
                                        </span>
                                    </label>
                                </>
                            )}

                            {/* Error surfaced with a Retry — never blocks
                                charging; the manual entry option below still
                                works. */}
                            {myCodes.error && !myCodes.loading && (
                                <p className="redeem-code-fetch-error" role="alert">
                                    Couldn't load your redeem codes.{' '}
                                    <button
                                        type="button"
                                        className="redeem-code-fetch-retry"
                                        onClick={loadMyCodes}
                                        disabled={isSubmitting}
                                    >
                                        Retry
                                    </button>
                                </p>
                            )}
                        </fieldset>
                    ) : (
                        // Guest / no-account-codes: the original toggle
                        // "Have a redeem code?" is preserved so the guest
                        // UX is unchanged.
                        !appliedCredit && !redeemPanelOpen && (
                            <button
                                type="button"
                                className="redeem-code-toggle"
                                onClick={() => setRedeemPanelOpen(true)}
                                disabled={isSubmitting}
                            >
                                Have a redeem code?
                            </button>
                        )
                    )}

                    {/* Manual entry panel — revealed for guests when the
                        toggle is clicked, or for authed users when the
                        "Enter another code" radio is selected. */}
                    {redeemPanelOpen && !appliedCredit && (
                        <div className="redeem-code-panel">
                            <label htmlFor="redeem-code" className="redeem-code-label">
                                Redeem code
                            </label>
                            <input
                                id="redeem-code"
                                type="text"
                                inputMode="text"
                                autoCapitalize="characters"
                                autoComplete="off"
                                value={redeemInput}
                                onChange={(e) => {
                                    setRedeemInput(e.target.value.toUpperCase());
                                    if (redeemError) setRedeemError('');
                                }}
                                className="redeem-code-input"
                                placeholder="Enter credit code"
                                disabled={isSubmitting || redeemStatus === 'validating'}
                            />
                            {redeemError && (
                                <p className="redeem-code-error" role="alert">{redeemError}</p>
                            )}
                            <div className="redeem-code-actions">
                                <button
                                    type="button"
                                    className="redeem-code-cancel"
                                    onClick={() => {
                                        setRedeemPanelOpen(false);
                                        setRedeemInput('');
                                        setRedeemStatus('idle');
                                        setRedeemError('');
                                    }}
                                    disabled={isSubmitting || redeemStatus === 'validating'}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="redeem-code-apply"
                                    onClick={handleApplyRedeemCode}
                                    disabled={isSubmitting || redeemStatus === 'validating' || !redeemInput.trim()}
                                >
                                    {redeemStatus === 'validating' ? 'Checking…' : 'Apply'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Applied summary. Preserved for both flows so a
                        manual-entered code (which never appears in the
                        account radio list) still surfaces its details
                        + Remove control. When the applied code IS an
                        account code, its radio also stays visually
                        checked above. */}
                    {appliedCredit && (
                        <div className="redeem-code-applied">
                            <div className="redeem-code-applied-title">Credit code applied</div>
                            <div className="redeem-code-applied-code">{appliedCredit.code}</div>
                            {/* Full code value — how much the admin issued.
                                The Charging Credit summary line below shows
                                the CAPPED amount actually applied against
                                the selected budget. */}
                            <div className="redeem-code-applied-amount">
                                Value: {Number(appliedCredit.amount).toFixed(2)} {appliedCredit.currency}
                            </div>
                            <button
                                type="button"
                                className="redeem-code-remove"
                                onClick={handleRemoveRedeemCode}
                                disabled={isSubmitting}
                            >
                                Remove
                            </button>
                        </div>
                    )}
                </div>

                {/* Inline nudge when the customer applied a credit but
                    hasn't picked a pre-auth budget yet. A budget is
                    mandatory — the credit reduces it, doesn't replace
                    it. Kept BEFORE the summary block since the summary
                    itself is only shown when both are present. */}
                {creditWithoutBudget && (
                    <p className="amount-picker-hint amount-picker-hint-warn" role="status">
                        Select a pre-authorization amount to apply this credit.
                    </p>
                )}

                {/* Adjusted authorization breakdown — only rendered when
                    BOTH a valid budget and a valid credit are present,
                    per spec. Server is source of truth; this is a
                    preview to make the reduced hold obvious BEFORE
                    the customer commits.

                    Charging Credit shows the CAPPED value
                    min(codeAmount, selectedBudget) — never more than
                    the selected authorization, because the code is
                    single-use and unused value does not carry forward. */}
                {appliedCredit && effectiveCents > 0 && (
                    <>
                        {isFullyCovered ? (
                            <Notice variant="success" icon={false} className="amount-picker-info">
                                <div style={{ fontWeight: 700, marginBottom: 4 }}>
                                    No Card Authorization Required
                                </div>
                                <dl className="amount-picker-summary amount-picker-summary-embedded">
                                    <dt>Selected Authorization</dt>
                                    <dd>{formatCurrency(effectiveCents, currency)}</dd>
                                    <dt>Charging Credit</dt>
                                    <dd>−{formatCurrency(creditAppliedCents, currency)}</dd>
                                </dl>
                            </Notice>
                        ) : (
                            <dl className="amount-picker-summary">
                                <dt>Selected Authorization</dt>
                                <dd>{formatCurrency(effectiveCents, currency)}</dd>
                                <dt>Charging Credit</dt>
                                <dd>−{formatCurrency(creditAppliedCents, currency)}</dd>
                                <dt className="amount-picker-summary-total">Card Authorization Needed</dt>
                                <dd className="amount-picker-summary-total">{formatCurrency(cardAuthCents, currency)}</dd>
                            </dl>
                        )}
                        {hasCarryForward && (
                            <p className="amount-picker-hint" role="status">
                                This one-time credit covers your selected authorization. Any unused credit value will not carry forward.
                            </p>
                        )}
                    </>
                )}

                <Notice variant="success" icon={false} className="amount-picker-info">
                    This is a temporary hold, not a final charge. You will only be billed for the energy you use. Any remaining balance is automatically released back to your card.
                </Notice>
            </div>

            <ActionButtonWrapper ref={ref} actions={actions} onClickAction={onContinue} />
        </div>
    );
}
